/**
 * Le parc, vu par l'écran : véhicules, remorques, chauffeurs.
 *
 * **Paramétré par un descripteur**, comme l'annuaire, et pour la même raison :
 * les trois écrans ne diffèrent que par leurs colonnes et leurs champs.
 *
 * **POURQUOI PAS `annuaire.ts`**, alors que la liste a la même forme. Trois
 * choses ne se recouvrent pas :
 *
 *   - une ligne d'annuaire a un nom, un code, une localité — colonnes écrites
 *     en dur dans `LigneListe` et dans le tableau. Une remorque a une
 *     immatriculation, un gabarit et une charge utile ; rien à mettre dans ces
 *     cases ;
 *   - une fiche d'annuaire a jusqu'à cinq onglets, des contacts, une
 *     couverture. Une fiche de parc a ses champs, et c'est tout ;
 *   - les chemins de l'API diffèrent : `/clients` d'un côté, `/parc/vehicules`
 *     de l'autre, en DEUX segments.
 *
 * Ce qui est partagé l'est vraiment : `aplatir`, `capitale` et `dateCourte`
 * viennent d'`annuaire.ts`, et le formulaire est `FormulaireFiche`, déjà
 * paramétré par une liste de champs. Le jour où un troisième écran aura besoin
 * d'un tableau à colonnes libres, c'est le tableau qu'on paramétrera — pas
 * avant, et pas en tordant l'annuaire pour deux cas.
 */

import { aplatir, type Champ } from './annuaire.js';

/**
 * Une valeur rendue par l'API pour une ligne de parc. `charge_utile` est un
 * `integer` en base et arrive donc en NOMBRE, là où `conditions_paiement` d'un
 * client est un `numeric` et arrive en chaîne — les deux existent, et ce type
 * dit lequel on peut rencontrer plutôt que de tout ramener à `unknown`.
 */
export type ValeurParc = string | number | boolean | null;

/** Une ligne du parc, telle que l'API la rend. */
export type LigneParc = Readonly<Record<string, ValeurParc>> & {
  readonly id: string;
  readonly actif: boolean;
};

/**
 * Un champ du formulaire de parc.
 *
 * `maxNombre` s'ajoute au `Champ` de l'annuaire, qui n'a que `max`, un nombre
 * de CARACTÈRES. Un annuaire n'a aucun entier ; le parc en a deux, et sans lui
 * l'écran laisserait passer 999 palettes que l'API refuserait — la faute
 * s'afficherait dans le bandeau au lieu de se poser sous le champ.
 */
export interface ChampParc extends Champ {
  /** Valeur maximale d'un entier. Reprend celle de `apps/api/src/moyens.ts`. */
  readonly maxNombre?: number;
  /**
   * Le champ porte un nombre À VIRGULE — la capacité en mètres linéaires,
   * ajoutée le 04/09/2026. `maxNombre` s'applique aussi, mais la saisie accepte
   * une virgule et deux décimales.
   *
   * Séparé de `maxNombre` seul : sans ce drapeau, « 13,20 » serait refusé par
   * le contrôle des entiers, et l'utilisateur devrait taper « 13.2 » — ce que
   * personne ne fait sur un clavier français.
   */
  readonly decimal?: boolean;
}

/** Une colonne du tableau. */
export interface Colonne {
  readonly cle: string;
  readonly entete: string;
  /**
   * Classe posée sur la colonne. `col-large` la fait disparaître sous 760 px
   * et quand une fiche est ouverte — c'est le seul mot que le style connaisse.
   */
  readonly classe?: string;
  /** Police à chasse fixe : immatriculations, quantités. */
  readonly chiffre?: boolean;
  /**
   * La colonne porte une CLÉ dont le libellé vient d'une liste chargée à
   * l'exécution — les types de véhicule, depuis le 04/09/2026. `gabarit`
   * ci-dessous fait la même chose pour le référentiel commun ; les deux
   * cohabitent le temps que le second rejoigne le premier.
   */
  readonly liste?: 'types_vehicule';
  /** Complément affiché en gris après la valeur — « 33 pal », « 24 t ». */
  readonly unite?: string;
  /**
   * La valeur est un CODE du référentiel commun des gabarits ; la colonne
   * affiche son libellé. La table ne porte que le code (`vehicule_code`) —
   * inventer une colonne `gabarit` qui n'existe pas en base serait mentir sur
   * ce que l'API rend.
   */
  readonly gabarit?: boolean;
}

export interface Parc {
  /** Chemin de l'API, sans barre initiale. Peut avoir DEUX segments. */
  readonly chemin: string;
  /** Clé sous laquelle l'API rend la liste. */
  readonly cleListe: string;
  /** Clé sous laquelle l'API rend une ligne — c'est le mot du journal. */
  readonly cleFiche: string;
  /** Clé du sous-menu de la barre haute. */
  readonly cle: string;
  readonly singulier: string;
  readonly pluriel: string;
  /**
   * Le mot est-il FÉMININ ? Une remorque, un véhicule, un chauffeur.
   *
   * Sans cette ligne, l'écran écrirait « Aucun remorque » ou porterait huit
   * fois `singulier === 'remorque'`, ce qui remettrait dans le dessin ce qu'un
   * descripteur est là pour en sortir — et se tromperait au premier parc
   * féminin de plus.
   */
  readonly feminin?: boolean;
  /** La colonne qui NOMME la ligne. Sert au titre de la fiche. */
  readonly colonneNom: string;
  readonly colonnes: readonly Colonne[];
  readonly champs: readonly ChampParc[];
  /** Les colonnes fouillées par la recherche. */
  readonly recherche: readonly string[];
  /**
   * Ce que la case de recherche annonce fouiller, EN FRANÇAIS.
   *
   * Écrit à la main plutôt que déduit de `recherche` : les clés sont des noms
   * de colonnes — « vehicule_code » s'est affiché tel quel sous les yeux, vu
   * en regardant l'écran le 03/09/2026.
   */
  readonly rechercheDite: string;
  /** Pied du bandeau affiché sur une ligne désactivée. */
  readonly noteDesactive: string;
}

// ---------------------------------------------------------------------------
// Lire une valeur
// ---------------------------------------------------------------------------

/**
 * Rend une valeur en TEXTE, ou `null` si elle est absente.
 *
 * `null` et la chaîne vide sont ramenés au même « rien » : la base accepte les
 * deux, et l'écran n'a aucune raison de les distinguer sous les yeux. `false`
 * en revanche n'est pas rien — c'est pourquoi les booléens ne passent pas par
 * ici mais par la colonne `actif`, lue directement.
 */
export function texte(ligne: LigneParc, cle: string): string | null {
  const brute = ligne[cle];
  if (brute === null || brute === undefined) return null;
  if (typeof brute === 'boolean') return brute ? 'oui' : 'non';
  const rendu = String(brute).trim();
  return rendu === '' ? null : rendu;
}

/**
 * La même valeur, MISE EN FORME pour être lue : « 24 000 » et non « 24000 ».
 *
 * Séparée de `texte` à dessein. `texte` sert aussi à remplir le FORMULAIRE, où
 * une espace de milliers ferait échouer le contrôle du champ — et où il faut
 * rendre exactement ce que la base contient. Ici on lit ; là on saisit.
 *
 * Seuls les vrais nombres sont mis en forme : une immatriculation est une
 * chaîne, et « GZ-083-NF » n'a pas de milliers.
 */
export function lisible(ligne: LigneParc, cle: string): string | null {
  const brute = ligne[cle];
  if (typeof brute === 'number' && Number.isFinite(brute)) {
    return new Intl.NumberFormat('fr-FR').format(brute);
  }
  return texte(ligne, cle);
}

/** Le nom d'une ligne, pour un titre. Jamais vide : la colonne est obligatoire. */
export function nommer(ligne: LigneParc, parc: Parc): string {
  return texte(ligne, parc.colonneNom) ?? 'sans nom';
}

// ---------------------------------------------------------------------------
// Filtrer, compter, ranger
// ---------------------------------------------------------------------------

/**
 * Filtre la liste déjà chargée, dans le navigateur — comme l'annuaire, et pour
 * la même raison : un parc tient en quelques dizaines de lignes.
 *
 * Chaque mot saisi doit se retrouver quelque part, dans n'importe quel ordre.
 */
export function filtrer(
  lignes: readonly LigneParc[],
  recherche: string,
  parc: Parc,
): readonly LigneParc[] {
  const mots = aplatir(recherche)
    .split(/\s+/)
    .filter((m) => m !== '');
  if (mots.length === 0) return lignes;

  return lignes.filter((ligne) => {
    const matiere = aplatir(
      parc.recherche
        .map((cle) => texte(ligne, cle))
        .filter((v): v is string => v !== null)
        .join(' '),
    );
    return mots.every((mot) => matiere.includes(mot));
  });
}

/** « 8 véhicules — dont 1 inactif, affiché en gris », ou l'une de ses variantes. */
export function decompte(lignes: readonly LigneParc[], parc: Parc): string {
  const total = lignes.length;
  const inactifs = lignes.filter((l) => !l.actif).length;
  const debut = `${total} ${total > 1 ? parc.pluriel : parc.singulier}`;

  if (inactifs === 0) return debut;

  // « inactif » ne devient pas « inactife » : le f devient v. Le mot est donc
  // écrit en entier plutôt que rallongé d'une marque — un test l'a montré, et
  // c'est ce qui arrive à qui accorde par concaténation.
  const marque = e(parc);
  const mot = inactif(parc);
  return inactifs > 1
    ? `${debut} — dont ${inactifs} ${mot}s, affiché${marque}s en gris`
    : `${debut} — dont 1 ${mot}, affiché${marque} en gris`;
}

/**
 * Range par la colonne qui nomme, les inactifs à la fin.
 *
 * L'API rend déjà la liste triée par cette colonne ; ce tri-ci n'en est pas un
 * second mais un REGROUPEMENT : ce qui ne roule plus descend au bas de la
 * liste au lieu d'être semé parmi ce qui roule. Même choix que pour l'annuaire.
 */
export function ranger(lignes: readonly LigneParc[], parc: Parc): readonly LigneParc[] {
  return [...lignes].sort((a, b) => {
    if (a.actif !== b.actif) return a.actif ? -1 : 1;
    return nommer(a, parc).localeCompare(nommer(b, parc), 'fr');
  });
}

// ---------------------------------------------------------------------------
// Le formulaire
// ---------------------------------------------------------------------------

/** Ce que le formulaire tient : une chaîne par champ, jamais `null`. */
export type Saisie = Readonly<Record<string, string>>;

export function saisieVide(parc: Parc): Saisie {
  return Object.fromEntries(parc.champs.map((c) => [c.cle, '']));
}

/** Remplit le formulaire à partir d'une ligne, pour la modification. */
export function saisieDepuis(ligne: LigneParc, parc: Parc): Saisie {
  return Object.fromEntries(parc.champs.map((c) => [c.cle, texte(ligne, c.cle) ?? '']));
}

/**
 * Contrôle la saisie AVANT l'envoi. Ce contrôle **anticipe** celui de l'API, il
 * ne le remplace pas : si les deux divergent, c'est l'API qui a raison.
 *
 * Rend une faute par champ fautif, vide si tout va bien.
 */
export function validerSaisie(
  saisie: Saisie,
  parc: Parc,
): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};
  for (const champ of parc.champs) {
    const valeur = (saisie[champ.cle] ?? '').trim();
    if (champ.obligatoire === true && valeur === '') {
      fautes[champ.cle] = `${champ.etiquette} est obligatoire.`;
      continue;
    }
    if (valeur === '') continue;

    if (champ.decimal === true) {
      // La VIRGULE est acceptée, et c'est le point : « 13,20 » est ce qu'on
      // tape. Deux décimales au plus, comme le `numeric(5, 2)` de la base.
      if (!/^\d+([.,]\d{1,2})?$/.test(valeur)) {
        fautes[champ.cle] = 'Un nombre, deux décimales au plus, sans unité.';
      } else if (
        champ.maxNombre !== undefined &&
        Number(valeur.replace(',', '.')) > champ.maxNombre
      ) {
        fautes[champ.cle] = `${champ.maxNombre} au plus.`;
      }
      continue;
    }

    if (champ.maxNombre !== undefined) {
      // Le champ porte un ENTIER. On refuse tout ce qui n'est pas une suite de
      // chiffres — « 12,5 » et « 12 t » compris : c'est l'unité qui est dans
      // l'étiquette, jamais dans la case.
      if (!/^\d+$/.test(valeur)) {
        fautes[champ.cle] = 'Un nombre entier, sans unité.';
      } else if (Number(valeur) > champ.maxNombre) {
        fautes[champ.cle] = `${champ.maxNombre} au plus.`;
      }
      continue;
    }

    if (valeur.length > champ.max) {
      fautes[champ.cle] = `${champ.max} caractères au plus.`;
    }
  }
  return fautes;
}

/**
 * Ce qui a changé, et rien d'autre.
 *
 * Un champ vidé part à `null` — c'est ce que l'API entend par « efface ». Un
 * champ inchangé n'est pas envoyé du tout : le journal des traces ne garde que
 * ce qui a bougé, et on n'écrase pas le travail d'un autre sur un champ qu'on
 * n'a pas ouvert.
 */
export function difference(
  initiale: Saisie,
  saisie: Saisie,
  parc: Parc,
): Readonly<Record<string, string | null>> {
  const change: Record<string, string | null> = {};
  for (const champ of parc.champs) {
    const avant = (initiale[champ.cle] ?? '').trim();
    const apres = (saisie[champ.cle] ?? '').trim();
    if (avant === apres) continue;
    change[champ.cle] = apres === '' ? null : apres;
  }
  return change;
}

/** Ce qu'on envoie à la création : les champs remplis, les vides omis. */
export function pourCreation(
  saisie: Saisie,
  parc: Parc,
): Readonly<Record<string, string | null>> {
  const corps: Record<string, string | null> = {};
  for (const champ of parc.champs) {
    const valeur = (saisie[champ.cle] ?? '').trim();
    if (valeur !== '') corps[champ.cle] = valeur;
  }
  return corps;
}

// ---------------------------------------------------------------------------
// L'accord
// ---------------------------------------------------------------------------

/** « un » ou « une ». */
export function un(parc: Parc): string {
  return parc.feminin === true ? 'une' : 'un';
}

/** « Aucun » ou « Aucune ». */
export function aucun(parc: Parc): string {
  return parc.feminin === true ? 'Aucune' : 'Aucun';
}

/** La marque du féminin à coller à un participe : « désactivé » + « e ». */
export function e(parc: Parc): string {
  return parc.feminin === true ? 'e' : '';
}

/** « Nouveau » ou « Nouvelle » — le titre du formulaire de création. */
export function nouveau(parc: Parc): string {
  return parc.feminin === true ? 'Nouvelle' : 'Nouveau';
}

/**
 * « inactif » ou « inactive ».
 *
 * Écrit en entier et non rallongé d'une marque : le f devient v. La même faute
 * avait produit « inactife » dans le décompte, et un test l'a montrée.
 */
export function inactif(parc: Parc): string {
  return parc.feminin === true ? 'inactive' : 'inactif';
}
